import { useState, type ReactNode } from 'react'
import { Plus, Search, Trash2, Download, Info, Edit } from 'lucide-react'
import {
  ChatBubble,
  QuoteSidebar,
  Badge,
  BetaBadge,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  DataTable,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownSeparator,
  DropdownTrigger,
  EmptyState,
  EmployeeAvatar,
  ErrorState,
  FilterBar,
  FilterChip,
  IconButton,
  Input,
  LoadingState,
  LoginCard,
  Modal,
  PageHeader,
  PriorityBadge,
  QualityScoreBar,
  RadioGroup,
  SegmentedControl,
  Select,
  SimpleTable,
  Spinner,
  StatusBadge,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  Tooltip,
  useToast,
  type ColumnDef,
} from '@/components/ui'
import { currencyColumn, dateColumn, statusColumn, textColumn } from '@/components/ui'

interface Section {
  id: string
  title: string
  description?: string
  preview: ReactNode
  code: string
}

function CodeBlock({ code }: { code: string }) {
  return (
    <pre className="mt-2 overflow-x-auto rounded-md bg-gray-900 text-white p-3 text-caption leading-relaxed">
      <code>{code}</code>
    </pre>
  )
}

function Row({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-center gap-3">{children}</div>
}

interface DemoRow {
  id: string
  name: string
  status: string
  priority: 'low' | 'normal' | 'high' | 'urgent'
  amount: number
  due: string
}

const demoRows: DemoRow[] = [
  { id: '1', name: 'Firmware Update Server 01', status: 'in_progress', priority: 'high', amount: 1200, due: '2026-05-04' },
  { id: '2', name: 'Lizenz Erneuerung Kunde A', status: 'open', priority: 'normal', amount: 499, due: '2026-04-28' },
  { id: '3', name: 'VPN-Ausfall Kunde B', status: 'urgent', priority: 'urgent', amount: 0, due: '2026-04-19' },
  { id: '4', name: 'Monitoring Setup', status: 'resolved', priority: 'low', amount: 340, due: '2026-04-15' },
  { id: '5', name: 'Jahresrechnung Kunde C', status: 'paid', priority: 'normal', amount: 8900, due: '2026-03-30' },
]

const demoColumns: ColumnDef<DemoRow, unknown>[] = [
  textColumn<DemoRow>('name', 'Thema'),
  statusColumn<DemoRow>('status'),
  { accessorKey: 'priority', header: 'Priorität', cell: ({ getValue }) => <PriorityBadge priority={getValue() as DemoRow['priority']} /> },
  currencyColumn<DemoRow>('amount', 'Betrag'),
  dateColumn<DemoRow>('due', 'Fällig'),
]

export function UiKitPage() {
  const [modalOpen, setModalOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [activeFilter, setActiveFilter] = useState<string>('all')
  const [switchOn, setSwitchOn] = useState(true)
  const [checked, setChecked] = useState(false)
  const [sel, setSel] = useState<string[]>([])
  const [radioValue, setRadioValue] = useState<'remote' | 'onsite'>('remote')
  const [segmentValue, setSegmentValue] = useState<'m365' | 'password'>('m365')
  const [pillValue, setPillValue] = useState<string>('09:00')
  const { toast } = useToast()

  const sections: Section[] = [
    {
      id: 'buttons',
      title: 'Button',
      description: 'Varianten: primary, secondary, ghost, danger, accent, link. Größen: xs, sm, md, lg.',
      preview: (
        <div className="flex flex-col gap-3">
          <Row>
            <Button>Primary</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="danger">Danger</Button>
            <Button variant="accent">Accent</Button>
            <Button variant="link">Link</Button>
            <Button variant="linkDanger">Link (Danger)</Button>
          </Row>
          <Row>
            <Button size="xs">xs</Button>
            <Button size="sm">sm</Button>
            <Button size="md">md</Button>
            <Button size="lg">lg</Button>
          </Row>
          <Row>
            <Button leadingIcon={<Plus />}>Neu</Button>
            <Button variant="secondary" trailingIcon={<Download />}>Exportieren</Button>
            <Button loading>Lädt …</Button>
            <Button disabled>Disabled</Button>
          </Row>
          <Row>
            <IconButton icon={<Edit />} aria-label="Bearbeiten" />
            <IconButton variant="secondary" icon={<Edit />} aria-label="Bearbeiten" />
            <IconButton variant="danger" icon={<Trash2 />} aria-label="Löschen" />
          </Row>
        </div>
      ),
      code: `<Button leadingIcon={<Plus />}>Neu</Button>
<Button variant="secondary">Abbrechen</Button>
<Button variant="danger" loading>Löschen</Button>
<IconButton icon={<Edit />} aria-label="Bearbeiten" />`,
    },
    {
      id: 'badges',
      title: 'Badge / StatusBadge / PriorityBadge',
      description: 'Status-Werte aus `styles/tokens.ts` mappen automatisch auf Badge-Varianten.',
      preview: (
        <div className="flex flex-col gap-3">
          <Row>
            <Badge>neutral</Badge>
            <Badge variant="info">info</Badge>
            <Badge variant="success">success</Badge>
            <Badge variant="warning">warning</Badge>
            <Badge variant="danger">danger</Badge>
            <Badge variant="brand">brand</Badge>
            <Badge variant="accent">accent</Badge>
            <BetaBadge />
          </Row>
          <Row>
            <StatusBadge status="open" />
            <StatusBadge status="in_progress" />
            <StatusBadge status="resolved" />
            <StatusBadge status="overdue" />
            <StatusBadge status="paid" />
            <StatusBadge status="cancelled" />
          </Row>
          <Row>
            <PriorityBadge priority="low" />
            <PriorityBadge priority="normal" />
            <PriorityBadge priority="high" />
            <PriorityBadge priority="urgent" />
          </Row>
        </div>
      ),
      code: `<StatusBadge status="in_progress" />
<PriorityBadge priority="high" />
<Badge variant="brand">Neu</Badge>`,
    },
    {
      id: 'quality-score-bar',
      title: 'QualityScoreBar',
      description: 'Fortschrittsbalken für Datenqualitäts-Scores. Farbcodiert: 0–49 rot, 50–74 orange, 75–99 gelb, 100 grün.',
      preview: (
        <div className="flex flex-col gap-3 max-w-xs">
          <QualityScoreBar score={18} />
          <QualityScoreBar score={55} />
          <QualityScoreBar score={82} />
          <QualityScoreBar score={100} />
        </div>
      ),
      code: `<QualityScoreBar score={82} />`,
    },
    {
      id: 'forms',
      title: 'Formulare (Input, Textarea, Select, Checkbox, Switch)',
      preview: (
        <div className="grid grid-cols-2 gap-4 max-w-2xl">
          <Input label="Name" placeholder="Max Mustermann" />
          <Input label="E-Mail" type="email" leadingIcon={<Search />} placeholder="name@firma.de" />
          <Input label="Mit Fehler" error="Pflichtfeld" required />
          <Select
            label="Kategorie"
            options={[
              { value: 'a', label: 'Hardware' },
              { value: 'b', label: 'Software' },
              { value: 'c', label: 'Dienstleistung' },
            ]}
          />
          <Textarea label="Beschreibung" placeholder="Details …" wrapperClassName="col-span-2" />
          <Row>
            <Checkbox label="Checkbox" checked={checked} onChange={(e) => setChecked(e.currentTarget.checked)} />
            <Checkbox label="Indeterminate" indeterminate readOnly />
            <Switch label="Switch" checked={switchOn} onChange={(e) => setSwitchOn(e.currentTarget.checked)} />
          </Row>
          <RadioGroup<'remote' | 'onsite'>
            label="Termin-Ort"
            value={radioValue}
            onValueChange={setRadioValue}
            options={[
              { value: 'remote', label: 'Remote', hint: 'Fernwartung per TeamViewer' },
              { value: 'onsite', label: 'Vor Ort' },
            ]}
          />
        </div>
      ),
      code: `<Input label="Name" placeholder="Max Mustermann" />
<Select label="Kategorie" options={[{ value: 'a', label: 'Hardware' }]} />
<Checkbox label="Aktivieren" />
<Switch label="Benachrichtigungen" />
<RadioGroup
  label="Termin-Ort"
  value={location}
  onValueChange={setLocation}
  options={[
    { value: 'remote', label: 'Remote' },
    { value: 'onsite', label: 'Vor Ort' },
  ]}
/>`,
    },
    {
      id: 'segmented-control',
      title: 'SegmentedControl',
      description:
        '2–4 gegenseitig ausschließende Optionen. `variant="tab"` füllt die Breite (z.B. Login-Mode), `variant="pill"` ist für lose Auswahl (z.B. Slot-Picker).',
      preview: (
        <div className="flex flex-col gap-4 max-w-md">
          <SegmentedControl<'m365' | 'password'>
            fullWidth
            value={segmentValue}
            onValueChange={setSegmentValue}
            options={[
              { value: 'm365', label: 'Microsoft 365' },
              { value: 'password', label: 'E-Mail & Passwort' },
            ]}
            aria-label="Login-Modus"
          />
          <SegmentedControl
            variant="pill"
            size="sm"
            value={pillValue}
            onValueChange={setPillValue}
            options={[
              { value: '08:00', label: '08:00' },
              { value: '09:00', label: '09:00' },
              { value: '10:00', label: '10:00' },
              { value: '11:00', label: '11:00' },
            ]}
            aria-label="Zeit-Slot"
          />
        </div>
      ),
      code: `<SegmentedControl
  fullWidth
  value={mode}
  onValueChange={setMode}
  options={[
    { value: 'm365', label: 'Microsoft 365' },
    { value: 'password', label: 'E-Mail & Passwort' },
  ]}
/>

<SegmentedControl
  variant="pill"
  size="sm"
  value={slot}
  onValueChange={setSlot}
  options={slots.map((s) => ({ value: s.start, label: s.label }))}
/>`,
    },
    {
      id: 'card',
      title: 'Card',
      preview: (
        <Card className="max-w-md">
          <Card.Header>
            <Card.Title>Offene Tickets</Card.Title>
            <Badge variant="brand">12</Badge>
          </Card.Header>
          <Card.Body>
            <p className="text-body text-gray-700">
              Zeigt eine Zusammenfassung der offenen Tickets für den aktuellen Nutzer.
            </p>
          </Card.Body>
          <Card.Footer>
            <Button variant="secondary" size="sm">Schließen</Button>
            <Button size="sm">Öffnen</Button>
          </Card.Footer>
        </Card>
      ),
      code: `<Card>
  <Card.Header><Card.Title>…</Card.Title></Card.Header>
  <Card.Body>…</Card.Body>
  <Card.Footer><Button>…</Button></Card.Footer>
</Card>`,
    },
    {
      id: 'states',
      title: 'EmptyState / LoadingState / ErrorState',
      preview: (
        <div className="grid md:grid-cols-3 gap-4">
          <Card>
            <Card.Body>
              <EmptyState title="Keine Tickets" description="Aktuell sind keine Tickets offen." />
            </Card.Body>
          </Card>
          <Card>
            <Card.Body>
              <LoadingState />
            </Card.Body>
          </Card>
          <Card>
            <Card.Body>
              <ErrorState description="Verbindung zu TANSS fehlgeschlagen." details={'at fetchTickets\n  at TicketsPage\n  at render'} onRetry={() => undefined} />
            </Card.Body>
          </Card>
        </div>
      ),
      code: `<EmptyState title="Keine Tickets" description="…" />
<LoadingState label="Lade Tickets …" />
<ErrorState description={error} details={stack} onRetry={refetch} />`,
    },
    {
      id: 'filterbar',
      title: 'FilterBar + FilterChip',
      preview: (
        <FilterBar>
          <FilterChip active={activeFilter === 'all'} onClick={() => setActiveFilter('all')} count={42}>
            Alle
          </FilterChip>
          <FilterChip active={activeFilter === 'open'} onClick={() => setActiveFilter('open')} count={12}>
            Offen
          </FilterChip>
          <FilterChip active={activeFilter === 'progress'} onClick={() => setActiveFilter('progress')} count={7}>
            In Bearbeitung
          </FilterChip>
          <FilterChip active={activeFilter === 'closed'} onClick={() => setActiveFilter('closed')} count={23}>
            Geschlossen
          </FilterChip>
        </FilterBar>
      ),
      code: `<FilterBar>
  <FilterChip active={f === 'all'} onClick={() => setF('all')} count={42}>Alle</FilterChip>
  <FilterChip active={f === 'open'} onClick={() => setF('open')} count={12}>Offen</FilterChip>
</FilterBar>`,
    },
    {
      id: 'tabs',
      title: 'Tabs',
      preview: (
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Übersicht</TabsTrigger>
            <TabsTrigger value="activity">Aktivität</TabsTrigger>
            <TabsTrigger value="settings">Einstellungen</TabsTrigger>
          </TabsList>
          <TabsContent value="overview">Übersicht-Inhalt</TabsContent>
          <TabsContent value="activity">Aktivitäts-Log</TabsContent>
          <TabsContent value="settings">Einstellungen</TabsContent>
        </Tabs>
      ),
      code: `<Tabs defaultValue="overview">
  <TabsList>
    <TabsTrigger value="overview">Übersicht</TabsTrigger>
    <TabsTrigger value="activity">Aktivität</TabsTrigger>
  </TabsList>
  <TabsContent value="overview">…</TabsContent>
</Tabs>`,
    },
    {
      id: 'dropdown-tooltip-toast',
      title: 'Dropdown / Tooltip / Toast',
      preview: (
        <Row>
          <Dropdown>
            <DropdownTrigger asChild>
              <Button variant="secondary">Aktionen ▾</Button>
            </DropdownTrigger>
            <DropdownContent align="start">
              <DropdownItem onSelect={() => toast({ title: 'Bearbeitet', tone: 'success' })}>
                Bearbeiten
              </DropdownItem>
              <DropdownItem onSelect={() => toast({ title: 'Dupliziert', tone: 'info' })}>
                Duplizieren
              </DropdownItem>
              <DropdownSeparator />
              <DropdownItem tone="danger" onSelect={() => toast({ title: 'Gelöscht', tone: 'danger' })}>
                Löschen
              </DropdownItem>
            </DropdownContent>
          </Dropdown>
          <Tooltip content="Hier steht eine Hilfe">
            <Button variant="ghost" leadingIcon={<Info />}>Hilfe</Button>
          </Tooltip>
          <Button
            variant="secondary"
            onClick={() =>
              toast({ title: 'Gespeichert', description: 'Die Änderungen wurden übernommen.', tone: 'success' })
            }
          >
            Toast anzeigen
          </Button>
        </Row>
      ),
      code: `<Dropdown>
  <DropdownTrigger asChild><Button>Aktionen ▾</Button></DropdownTrigger>
  <DropdownContent>
    <DropdownItem onSelect={edit}>Bearbeiten</DropdownItem>
    <DropdownItem tone="danger" onSelect={del}>Löschen</DropdownItem>
  </DropdownContent>
</Dropdown>

const { toast } = useToast()
toast({ title: 'Gespeichert', tone: 'success' })`,
    },
    {
      id: 'modal',
      title: 'Modal / ConfirmDialog',
      preview: (
        <Row>
          <Button onClick={() => setModalOpen(true)}>Modal öffnen</Button>
          <Button variant="danger" onClick={() => setConfirmOpen(true)}>
            Bestätigung anzeigen
          </Button>
          <Modal
            open={modalOpen}
            onOpenChange={setModalOpen}
            title="Neue Aufgabe"
            description="Fülle die Felder aus, um eine neue Aufgabe anzulegen."
            footer={
              <>
                <Button variant="secondary" size="sm" onClick={() => setModalOpen(false)}>
                  Abbrechen
                </Button>
                <Button size="sm" onClick={() => setModalOpen(false)}>
                  Speichern
                </Button>
              </>
            }
          >
            <div className="flex flex-col gap-3">
              <Input label="Titel" placeholder="Kurze Beschreibung" />
              <Textarea label="Beschreibung" />
            </div>
          </Modal>
          <ConfirmDialog
            open={confirmOpen}
            title="Eintrag löschen?"
            message="Diese Aktion kann nicht rückgängig gemacht werden."
            onConfirm={() => {
              toast({ title: 'Gelöscht', tone: 'success' })
              setConfirmOpen(false)
            }}
            onCancel={() => setConfirmOpen(false)}
          />
        </Row>
      ),
      code: `<Modal open={open} onOpenChange={setOpen} title="Neue Aufgabe" footer={…}>
  <Input label="Titel" />
</Modal>

<ConfirmDialog
  open={open}
  title="Löschen?"
  message="Nicht rückgängig machbar"
  onConfirm={del}
  onCancel={() => setOpen(false)}
/>`,
    },
    {
      id: 'datatable',
      title: 'DataTable',
      description:
        'Suchen, Sortieren, Paginieren, Zeilenauswahl, Expandable Rows – alles zentral. Die globale Suche ist per Default aktiv (`searchable={false}` zum Deaktivieren, z.B. bei server-seitiger Filterung).',
      preview: (
        <DataTable<DemoRow>
          data={demoRows}
          columns={demoColumns}
          getRowId={(r) => r.id}
          enableRowSelection
          selectedRowIds={sel}
          onSelectedRowIdsChange={setSel}
          pagination={{ pageSize: 5 }}
          renderExpandedRow={(row) => (
            <div className="text-small text-gray-700">
              Zusatz-Details zu <strong>{row.original.name}</strong> (ID {row.original.id}).
            </div>
          )}
          toolbar={
            <div className="flex items-center justify-between w-full">
              <span className="text-small text-gray-600">
                {sel.length > 0 ? `${sel.length} ausgewählt` : 'Keine Auswahl'}
              </span>
              <Button size="sm" variant="secondary" leadingIcon={<Download />}>
                Exportieren
              </Button>
            </div>
          }
        />
      ),
      code: `<DataTable
  data={rows}
  columns={[
    textColumn('name', 'Thema'),
    statusColumn('status'),
    currencyColumn('amount', 'Betrag'),
    dateColumn('due', 'Fällig'),
  ]}
  getRowId={(r) => r.id}
  enableRowSelection
  pagination={{ pageSize: 25 }}
  // searchable={true}                // per Default an
  // searchPlaceholder="Projekte suchen …"
  // searchable={false}               // deaktivieren bei server-seitiger Filterung
/>`,
    },
    {
      id: 'simpletable',
      title: 'SimpleTable',
      description: 'Für rein statische Micro-Listen ohne Interaktion.',
      preview: (
        <SimpleTable>
          <SimpleTable.Head>
            <SimpleTable.Tr>
              <SimpleTable.Th>Modul</SimpleTable.Th>
              <SimpleTable.Th>Status</SimpleTable.Th>
            </SimpleTable.Tr>
          </SimpleTable.Head>
          <SimpleTable.Body>
            <SimpleTable.Tr>
              <SimpleTable.Td>TANSS</SimpleTable.Td>
              <SimpleTable.Td><StatusBadge status="active" /></SimpleTable.Td>
            </SimpleTable.Tr>
            <SimpleTable.Tr>
              <SimpleTable.Td>SAP</SimpleTable.Td>
              <SimpleTable.Td><StatusBadge status="active" /></SimpleTable.Td>
            </SimpleTable.Tr>
            <SimpleTable.Tr>
              <SimpleTable.Td>ESET</SimpleTable.Td>
              <SimpleTable.Td><StatusBadge status="error" /></SimpleTable.Td>
            </SimpleTable.Tr>
          </SimpleTable.Body>
        </SimpleTable>
      ),
      code: `<SimpleTable>
  <SimpleTable.Head><SimpleTable.Tr>…</SimpleTable.Tr></SimpleTable.Head>
  <SimpleTable.Body>
    <SimpleTable.Tr>
      <SimpleTable.Td>TANSS</SimpleTable.Td>
      <SimpleTable.Td><StatusBadge status="active" /></SimpleTable.Td>
    </SimpleTable.Tr>
  </SimpleTable.Body>
</SimpleTable>`,
    },
    {
      id: 'login-card',
      title: 'LoginCard',
      description:
        'Komplette Anmelde-Seite (Microsoft 365 + E-Mail/Passwort). Wird vom internen SysMan-Login und vom Kundenportal-Login benutzt — jede Layout-Änderung erfolgt zentral hier.',
      preview: (
        <LoginCard
          title="implec Kundenportal"
          subtitle="Anmeldung"
          iconClassName="bg-accent-500"
          m365LoginUrl="#"
          wrapperClassName="min-h-[520px]"
          onPasswordLogin={async () => {
            /* preview-only */
          }}
          footerHint="Du hast noch keinen Zugang? Bitte wende dich an deinen Ansprechpartner bei implec."
        />
      ),
      code: `<LoginCard
  title="implec Kundenportal"
  subtitle="Anmeldung"
  iconClassName="bg-accent-500"          // optional, default bg-brand-700
  m365LoginUrl="/api/auth/customer/m365/login"
  errorMessages={errorMessages}          // ?error=<code> → Text
  footerHint="… Hinweis unter dem Formular …"
  onPasswordLogin={async (email, password) => {
    await login(email, password)         // wirft bei Fehler
    navigate('/dashboard')
  }}
/>`,
    },
    {
      id: 'chat-bubble',
      title: 'ChatBubble',
      description:
        'Chat-Nachrichten für den KI-Dialog. Varianten: user (rechts), assistant (links), tool (kollabierbar, Vorbereitung Tool-Use).',
      preview: (
        <div className="flex flex-col gap-3 max-w-2xl">
          <ChatBubble role="user" content="Ich brauche einen Laptop für einen neuen Mitarbeiter." timestamp="10:04" />
          <ChatBubble
            role="assistant"
            content="Gerne! Für welchen Einsatzzweck ist der Laptop gedacht — Büroarbeit, unterwegs oder beides?"
            timestamp="10:04"
          />
          <ChatBubble
            role="tool"
            toolName="lookup_standard"
            toolInput={{ category: 'laptop-office' }}
            toolOutput={{ preferredVendor: 'Lenovo', minRamGb: 16 }}
            content="Standard geladen"
            timestamp="10:05"
          />
        </div>
      ),
      code: `<ChatBubble role="user" content="Ihre Nachricht" timestamp="10:04" />
<ChatBubble role="assistant" content="KI-Antwort" timestamp="10:04" />
<ChatBubble
  role="tool"
  toolName="lookup_standard"
  toolInput={{ category: 'laptop-office' }}
  toolOutput={{ preferredVendor: 'Lenovo' }}
  content="Standard geladen"
/>`,
    },
    {
      id: 'quote-sidebar',
      title: 'QuoteSidebar',
      description:
        'Live-Positionsliste für den KI-Angebotsdialog. Zeigt Artikel, Dienstleistungen und Hinweistexte mit editierbaren Mengen/Preisen/Rabatten und Netto-Summe.',
      preview: (
        <div className="w-80 border border-gray-200 rounded-xl overflow-hidden shadow-card h-96">
          <QuoteSidebar
            quoteId="demo"
            loading={false}
            error={null}
            onRefetch={() => {}}
            positions={[
              {
                id: '1',
                quoteId: 'demo',
                sortIndex: 0,
                sapItemCode: 'LENOVOT16-001',
                description: 'Lenovo ThinkPad T16 Gen 3',
                lineText: null,
                quantity: '5',
                unitPrice: '1249.00',
                discountPct: '5',
                kind: 'article',
                source: 'ai',
                standardId: null,
                needsSapCreation: false,
                isOptional: false,
                purchasePrice: null,
                parentPositionId: null,
                createdAt: new Date().toISOString(),
              },
              {
                id: '2',
                quoteId: 'demo',
                sortIndex: 1,
                sapItemCode: null,
                description: 'Inbetriebnahme und Konfiguration',
                lineText: null,
                quantity: '5',
                unitPrice: '250.00',
                discountPct: '0',
                kind: 'service',
                source: 'ai',
                standardId: null,
                needsSapCreation: false,
                isOptional: false,
                purchasePrice: null,
                parentPositionId: null,
                createdAt: new Date().toISOString(),
              },
              {
                id: '3',
                quoteId: 'demo',
                sortIndex: 2,
                sapItemCode: null,
                description: 'Inklusive 3 Jahre Vor-Ort-Garantie',
                lineText: null,
                quantity: '1',
                unitPrice: null,
                discountPct: '0',
                kind: 'note',
                source: 'manual',
                standardId: null,
                needsSapCreation: false,
                isOptional: false,
                purchasePrice: null,
                parentPositionId: null,
                createdAt: new Date().toISOString(),
              },
            ]}
          />
        </div>
      ),
      code: `<QuoteSidebar
  quoteId={quoteId}
  positions={positions}
  loading={loading}
  error={error}
  onRefetch={refetch}
/>`,
    },
    {
      id: 'employee-avatar',
      title: 'EmployeeAvatar',
      description: 'Rundes Avatar-Element für Mitarbeiter. Zeigt Foto, TANSS-Initialen oder abgeleitete Initialen. Hintergrundfarbe ist deterministisch aus dem Namen abgeleitet.',
      preview: (
        <div className="flex flex-col gap-4">
          <Row>
            <EmployeeAvatar name="Max Mustermann" size="xs" />
            <EmployeeAvatar name="Max Mustermann" size="sm" />
            <EmployeeAvatar name="Max Mustermann" size="md" />
            <EmployeeAvatar name="Max Mustermann" size="lg" />
          </Row>
          <Row>
            <EmployeeAvatar name="Anna Bauer" />
            <EmployeeAvatar name="Klaus Dieter" />
            <EmployeeAvatar name="Sarah Müller" />
            <EmployeeAvatar name="Thomas Keller" />
            <EmployeeAvatar name="Lisa Vogel" />
            <EmployeeAvatar name="Marco Schulz" />
          </Row>
          <Row>
            <EmployeeAvatar name="Maximilian Muster" initials="MM" />
            <EmployeeAvatar name="Extern Nutzer" initials="EN" />
          </Row>
          <Row>
            <EmployeeAvatar name="Mit Foto" avatarUrl="https://i.pravatar.cc/150?img=12" />
            <EmployeeAvatar name="Kaputtes Bild" avatarUrl="https://broken-url.invalid/photo.jpg" />
          </Row>
        </div>
      ),
      code: `// Initialen deterministisch aus Name
<EmployeeAvatar name="Max Mustermann" />

// TANSS-Initialen explizit setzen
<EmployeeAvatar name="..." initials="MM" />

// Foto mit automatischem Fallback auf Initialen bei 404
<EmployeeAvatar name="..." avatarUrl="/photo.jpg" />

// Größen: xs | sm | md | lg
<EmployeeAvatar name="..." size="sm" />`,
    },
    {
      id: 'misc',
      title: 'Misc (Spinner, Tooltip inline)',
      preview: (
        <Row>
          <Spinner size="sm" />
          <Spinner />
          <Spinner size="lg" />
        </Row>
      ),
      code: `<Spinner size="sm" />\n<Spinner />\n<Spinner size="lg" />`,
    },
  ]

  return (
    <div className="py-4 px-4 md:px-6 max-w-6xl mx-auto">
      <PageHeader
        title="UI-Kit"
        description="Alle zentralen Komponenten und Design-Tokens. Benutze diese statt eigener Tailwind-Blöcke."
        breadcrumbs={[{ label: 'Intern' }, { label: 'UI-Kit' }]}
      />

      {/* Navigation */}
      <nav className="mb-4 flex flex-wrap gap-2">
        {sections.map((s) => (
          <a
            key={s.id}
            href={`#${s.id}`}
            className="text-small text-brand-700 hover:text-brand-800 underline-offset-2 hover:underline"
          >
            {s.title}
          </a>
        ))}
      </nav>

      <div className="flex flex-col gap-8">
        {sections.map((s) => (
          <section key={s.id} id={s.id} className="scroll-mt-16">
            <Card>
              <Card.Header>
                <Card.Title>{s.title}</Card.Title>
              </Card.Header>
              <Card.Body>
                {s.description ? (
                  <p className="text-small text-gray-600 mb-4">{s.description}</p>
                ) : null}
                <div className="rounded-md border border-gray-200 bg-gray-50/50 p-4">
                  {s.preview}
                </div>
                <CodeBlock code={s.code} />
              </Card.Body>
            </Card>
          </section>
        ))}
      </div>
    </div>
  )
}
